import React, { useState, useMemo } from "react";
import {
    BackArrowIcon,
    BlueDownloadExportIcon,
    DownArrowSelect,
    DownloadExportIcon,
    PaginationLeftArrow,
    PaginationRightArrow,
    RefreshAddIcon,
    RefreshWhiteIcon,
    TrustCenterIcon,
    WhiteBellIcon,
} from "../../assets/icons";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const planOptions = [
    { value: "all", label: "All plans" },
    { value: "starter", label: "Starter" },
    { value: "growth", label: "Growth" },
];

const tenantSummaryOptions = [
    { value: "all", label: "All Tenants" },
    { value: "active", label: "Active" },
    { value: "trial", label: "Trial" },
];

const tenants = [
    {
        control: "Tech.inc",
        controlLabel: "Growth",
        status: "Complaint",
        riskValue: "Low",
        lastCheck: "12/09/2025",
        evidence: "MFA enabled",
    },
];

const Security = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedPlan, setSelectedPlan] = useState(planOptions[0]);
    const [selectedSummary, setSelectedSummary] = useState(tenantSummaryOptions[0]);
    const [selectedDate, setSelectedDate] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Filter tenants
    const filteredTenants = useMemo(() => {
        let result = tenants;

        // Plan filter
        if (selectedPlan.value !== "all") {
            result = result.filter((t) => t.plan.toLowerCase() === selectedPlan.value);
        }

        // Summary filter
        if (selectedSummary.value === "active") {
            result = result.filter((t) => t.status === "Active");
        } else if (selectedSummary.value === "trial") {
            result = result.filter((t) => t.status === "Trial");
        }

        // Date filter
        if (selectedDate.length === 2) {
            const [start, end] = selectedDate;
            result = result.filter((t) => {
                const lastActiveDate = dayjs(t.lastActive);
                return lastActiveDate.isAfter(start, "day") && lastActiveDate.isBefore(end, "day");
            });
        }

        return result;
    }, [selectedPlan, selectedSummary, selectedDate]);

    const totalCount = filteredTenants.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    const paginatedTenants = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredTenants.slice(start, start + pageSize);
    }, [filteredTenants, currentPage, pageSize]);

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setCurrentPage(1);
    };

    const goToPage = (pageNum) => {
        if (pageNum < 1 || pageNum > totalPages) return;
        setCurrentPage(pageNum);
    };

    return (
        <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] space-y-8 pb-6">
            {/* Header */}
            <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
                <div className="mb-4">
                    <button
                        type="button"
                        className="text-[16px] font-normal flex gap-1"
                        onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 1))}
                    >
                        {BackArrowIcon} Back
                    </button>
                </div>
                <div className="heading flex gap-2 justify-between items-end">
                    <div className="heading-title space-y-1">
                        <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
                            Security & Compliance
                        </h1>
                        <p className="text-span text-dark mb-0">
                            Vanta-style Continuous Security Compliance Monitoring
                        </p>
                    </div>
                </div>
            </div>

            <div className="sm:p-6 p-3 !pt-0">

                <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between bg-[#EDF7FF] rounded-xl sm:p-6 p-3 mb-6">
                    {/* ICON & DETAILS */}
                    <div className="grid md:flex items-start gap-2">
                        <span className="min-w-[40px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <rect width="40" height="40" rx="20" fill="#4285F4"></rect>
                                <path d="M20.0003 28.3337C24.6027 28.3337 28.3337 24.6027 28.3337 20.0003C28.3337 15.398 24.6027 11.667 20.0003 11.667C15.398 11.667 11.667 15.398 11.667 20.0003C11.667 24.6027 15.398 28.3337 20.0003 28.3337Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M20 23.333V19.583" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M20 16.6763V16.668" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                        </span>
                        <div>
                            <span className="text-[16px] font-semibold mb-2 block">
                                Governance-Centric Discount Control
                            </span>
                            <p className="text-gray-500 text-sm mb-0 w-[100%] xl:w-[80%] lg:w-[90%]">
                                Manage promotional programs, unique codes, tenant applications, and analytics with built-in fraud prevention and audit trails. Integrates with Stripe/Paddle for seamless billing operations.
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <button className="btn-light-blue">{WhiteBellIcon} Security</button>
                            <button className="btn-light-gray !text-[#4285F4]">{BlueDownloadExportIcon} Alerts</button>
                        </div>
                    </div>
                </div>
                {/* ==== */}
                <div className="grid grid-cols-1 custom-grid-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div className="chart-card !shadow-none">
                        <p className="mb-2 text-sm">Security score</p>
                        <p className="text-blue-600 text-xl font-bold mb-0">92%</p>
                    </div>
                    <div className="chart-card !shadow-none">
                        <p className="mb-2 text-sm">Trust score</p>
                        <p className="text-blue-600 text-xl font-bold mb-0">94%</p>
                    </div>
                    <div className="chart-card !shadow-none">
                        <p className="mb-2 text-sm">Control passing</p>
                        <p className="text-[#EA4335] text-xl font-bold mb-0">4/7 </p>
                    </div>
                    <div className="chart-card !shadow-none">
                        <p className="mb-2 text-sm">Open Remediation Tasks</p>
                        <p className="text-[#52C41A] text-xl font-bold mb-0">2</p>
                    </div>
                </div>
                {/* KPI Cards */}
                {/* Filters */}
                <div className="chart-card mt-6">
                    <h2 className="text-span text-black !font-semibold mb-0">Compliance Frameworks</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-8">
                        <div className="ComplianceFrameworks grid gap-2 p-4 border-[1px] border-solid border-[#F0F0F0] rounded-[10px]">
                            <p className="text-sm font-semibold mb-0">Security score</p>
                            <label className="px-2 py-1 bg-[#FFE4D7] w-fit border-b text-[#F45C0C] text-[12px] rounded-[5px]">Complaint</label>
                            <div className="flex items-center gap-2">
                                <div className="w-full h-[10px] bg-gray-100 rounded"><div className="h-[10px] bg-green-500 rounded" style={{ width: "87%" }}></div></div>
                                <p className="text-[#5C5B62] text-[14px] mb-0">87%</p>
                            </div>
                        </div>
                        <div className="ComplianceFrameworks grid gap-2 p-4 border-[1px] border-solid border-[#F0F0F0] rounded-[10px]">
                            <p className="text-sm font-semibold mb-0">Security score</p>
                            <label className="px-2 py-1 bg-[#EBFCFF] w-fit border-b text-[#0CD1F4] text-[12px] rounded-[5px]">Complaint</label>
                            <div className="flex items-center gap-2">
                                <div className="w-full h-[10px] bg-gray-100 rounded"><div className="h-[10px] bg-[#0CD1F4] rounded" style={{ width: "87%" }}></div></div>
                                <p className="text-[#5C5B62] text-[14px] mb-0">87%</p>
                            </div>
                        </div> <div className="ComplianceFrameworks grid gap-2 p-4 border-[1px] border-solid border-[#F0F0F0] rounded-[10px]">
                            <p className="text-sm font-semibold mb-0">Security score</p>
                            <label className="px-2 py-1 bg-[#FFE4D7] w-fit border-b text-[#F45C0C] text-[12px] rounded-[5px]">Complaint</label>
                            <div className="flex items-center gap-2">
                                <div className="w-full h-[10px] bg-gray-100 rounded"><div className="h-[10px] bg-green-500 rounded" style={{ width: "87%" }}></div></div>
                                <p className="text-[#5C5B62] text-[14px] mb-0">87%</p>
                            </div>
                        </div>
                        <div className="ComplianceFrameworks grid gap-2 p-4 border-[1px] border-solid border-[#F0F0F0] rounded-[10px]">
                            <p className="text-sm font-semibold mb-0">Security score</p>
                            <label className="px-2 py-1 bg-#FAFAFA w-fit border-b text-[#000000] text-[12px] rounded-[5px]">Complaint</label>
                            <div className="flex items-center gap-2">
                                <div className="w-full h-[10px] bg-gray-100 rounded"><div className="h-[10px] bg-green-500 rounded" style={{ width: "87%" }}></div></div>
                                <p className="text-[#5C5B62] text-[14px] mb-0">87%</p>
                            </div>
                        </div> <div className="ComplianceFrameworks grid gap-2 p-4 border-[1px] border-solid border-[#F0F0F0] rounded-[10px]">
                            <p className="text-sm font-semibold mb-0">Security score</p>
                            <label className="px-2 py-1 bg-[#FFE4D7] w-fit border-b text-[#F45C0C] text-[12px] rounded-[5px]">Complaint</label>
                            <div className="flex items-center gap-2">
                                <div className="w-full h-[10px] bg-gray-100 rounded"><div className="h-[10px] bg-[#EA4335] rounded" style={{ width: "87%" }}></div></div>
                                <p className="text-[#5C5B62] text-[14px] mb-0">87%</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="chart-card mt-6">
                    <h2 className="text-span text-black !font-semibold mb-0">Compliance Frameworks</h2>
                    <div className="overflow-x-auto bg-white rounded-md common-scroll mt-8">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[#F1F6FF] border-b border-[#F3EFEF]">
                                    <th className="px-4 py-3 text-left">Control</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Risk Value</th>
                                    <th className="px-4 py-3 text-left">Lastcheck</th>
                                    <th className="px-4 py-3 text-left">Evidence</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedTenants.map((t, i) => (
                                    <tr
                                        key={i}
                                        className="transition-colors border-b border-[#F3EFEF] last:border-b-0"
                                    >
                                        <td className="px-4 py-3">
                                            <span className=" !p-0 !text-[14px]">
                                                {t.control}
                                            </span>
                                            <span
                                                className="rounded-full bg-[#FFF4EE] text-[#F45C0C] block mt-1 w-fit"
                                            >
                                                {t.controlLabel}

                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className="rounded-full bg-[#FAFAFA] text-[#5C5B62]"
                                            >
                                                {t.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 ">
                                            <span
                                                className="rounded-full bg-[#E9FFDF] text-[#52C41A]"
                                            >
                                                {t.riskValue}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">{t.lastCheck}</td>
                                        <td className="px-4 py-3">{t.evidence}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* New Three Column Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
                    {/* Remediation Tasks */}
                    <div className="chart-card">
                        <h2 className="flex justify-between items-center text-span text-black !font-semibold mb-0 mb-4">Remediation Tasks

                            <button className="px-2 py-1 bg-[#FAFAFA] border-[1px] border-solid border-[#F2F2F2] w-fit text-[#000000] text-[14px] rounded-[8px] flex items-center gap-2">
                                {RefreshAddIcon}
                                Add task
                            </button>
                        </h2>


                        <div className="space-y-7">
                            {/* Task 1 */}
                            <div className="">
                                <div className="flex justify-between items-baseline mb-2">
                                    <p className="font-medium text-black text-sm m-0">Complete Quarterly Access Review</p>
                                    <span className=" bg-orange-100 text-orange-600 rounded-full full-rounded">Medium</span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">Review and update user access permissions for Q3 2025</p>
                                <div className="flex justify-between text-xs text-[#7C7C7C]">
                                    <span>Assigned to: Security Team</span>
                                    <span>Due: Sep 15</span>
                                </div>
                            </div>

                            {/* Task 2 */}
                            <div className="">
                                <div className="flex justify-between items-baseline mb-2">
                                    <p className="font-medium text-black text-sm m-0">Complete Quarterly Access Review</p>
                                    <span className="rounded-full full-rounded bg-[#FFF4F4] text-[#EA4335] ">Medium</span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">Review and update user access permissions for Q3 2025</p>
                                <div className="flex justify-between text-xs text-[#7C7C7C]">
                                    <span>Assigned to: Security Team</span>
                                    <span>Due: Sep 15</span>
                                </div>
                            </div>

                            {/* Task 3 */}
                            <div className="">
                                <div className="flex justify-between items-baseline mb-2">
                                    <p className="font-medium text-black text-sm m-0">Complete Quarterly Access Review</p>
                                    <span className="rounded-full full-rounded bg-orange-100 text-orange-600">Medium</span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">Review and update user access permissions for Q3 2025</p>
                                <div className="flex justify-between text-xs text-[#7C7C7C]">
                                    <span>Assigned to: Security Team</span>
                                    <span>Due: Sep 15</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Evidence Collection */}
                    <div className="chart-card">
                        <h2 className="flex justify-between items-center text-span text-black !font-semibold mb-0 mb-4">Evidence Collection

                            <button className="px-2 py-1 bg-[#FAFAFA] border-[1px] border-solid border-[#F2F2F2] w-fit text-[#000000] text-[14px] rounded-[8px] flex items-center gap-2">
                                {RefreshAddIcon}
                                Add task
                            </button>
                        </h2>


                        <div className="space-y-2">
                            <div className="chart-card !shadow-none !p-[20px]">
                                <p className="mb-2 text-sm">Security score</p>
                                <p className="text-blue-600 text-xl font-bold mb-0">92%</p>
                            </div>
                            <div className="chart-card !shadow-none !p-[20px]">
                                <p className="mb-2 text-sm">Trust score</p>
                                <p className="text-blue-600 text-xl font-bold mb-0">94%</p>
                            </div>
                            <div className="chart-card !shadow-none !p-[20px]">
                                <p className="mb-2 text-sm">Control passing</p>
                                <p className="text-[#EA4335] text-xl font-bold mb-0">4/7 </p>
                            </div>
                            {/* View Evidence Collection Button */}
                            <button className="px-5 py-4 justify-center bg-[#FAFAFA] border-[1px] border-solid border-[#F2F2F2] text-[#000000] text-[14px] rounded-[8px] flex items-center gap-2 w-full">
                                {RefreshAddIcon}
                                View evidence collection
                            </button>
                        </div>
                    </div>

                    {/* Trust Center */}
                    <div className="chart-card">
                        <h2 className="flex justify-between items-center text-span text-black !font-semibold mb-0 mb-4">Trust center</h2>

                        <div className="space-y-4">
                            {/* Compliance Ready */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                    {TrustCenterIcon}
                                    </div>
                                    <div>
                                        <p className="font-medium text-black mb-1">Compliance Ready</p>
                                        <p className="text-sm text-gray-600 mb-0 text-[#888076]">Your security posture meets SOC2 and GDPR requirements</p>
                                    </div>
                                </div>
                            </div>

                            {/* Download SOC2 Report */}
                            <button className="px-5 py-4 justify-center bg-[#FAFAFA] border-[1px] border-solid border-[#F2F2F2] text-[#000000] text-[14px] rounded-[8px] flex items-center gap-2 w-full">
                                {RefreshAddIcon}
                                View evidence collection
                            </button>

                            {/* Generate Trust Snapshot */}
                            <button className="px-5 py-4 justify-center bg-[#FAFAFA] border-[1px] border-solid border-[#F2F2F2] text-[#000000] text-[14px] rounded-[8px] flex items-center gap-2 w-full">
                                {RefreshAddIcon}
                                Generate trust snapshot
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Security;
